import { Navigate, useParams } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { Badge, Select } from '@/ui'
import {
  stepLabel,
  traceRows,
  unitHref,
  unitTypeLabel,
  useBatch,
  useTrace,
  type BatchDetail,
  type BatchRelative,
} from '../../api/process'
import { ExceptionTable } from '../../components/ExceptionTable'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { Code, Facts, Kg, RefLink, Section, StatTile, Status, Stream, When } from '../../components/bits'
import { statusLabel } from '../../labels'
import { useUrlState } from '../../lib/useUrlState'
import { holdText } from '../../model/process/hold'

type Tab = 'details' | 'back' | 'forward'
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'back', label: 'Trace back' },
  { id: 'forward', label: 'Trace forward' },
]

/** One batch (any run output): quantity, run, parents and children, sacks, QC samples, problems, trace. */
export function BatchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useBatch(id)
  const b = q.data
  if (b && b.unitType === 'LOT') return <Navigate to={`/c/lots/${b.id}`} replace />
  return (
    <ConsolePage
      back={{ to: '/c/batches', label: 'Batches' }}
      eyebrow="Process · Batch"
      title={b ? <Code className="text-[inherit]">{b.code}</Code> : 'Batch'}
      description={b ? `${unitTypeLabel(b.unitType)}${b.cropName ? ` · ${b.cropName}` : ''}${b.runCode ? ` · from run ${b.runCode}` : ''}` : undefined}
      actions={
        b ? (
          <span className="flex flex-wrap items-center gap-2">
            <Status status={b.status} />
            <Stream stream={b.stream} />
            {b.hold !== 'NONE' && <Status status={b.hold} label={holdText(b.hold, b.holdReasons)} />}
          </span>
        ) : undefined
      }
    >
      <QueryState query={q} label="this batch">
        {b && <BatchBody b={b} />}
      </QueryState>
    </ConsolePage>
  )
}

function BatchBody({ b }: { b: BatchDetail }) {
  const [s, setS] = useUrlState({ tab: 'details', depth: 32 })
  const tab = (TABS.some((t) => t.id === s.tab) ? s.tab : 'details') as Tab
  const qc = b.samples.find((x) => x.kind === 'BATCH_QC') ?? b.samples[0] ?? null
  const openProblems = b.problems.filter((p) => p.status === 'OPEN').length
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Quantity" value={<Kg grams={b.netG} className="text-[inherit]" />} hint="after the sacks' own weight" />
        <StatTile label="Sacks" value={b.sacks.filter((x) => x.status !== 'VOID').length} hint={`${b.parents.length} parent${b.parents.length === 1 ? '' : 's'} · ${b.children.length} made from it`} />
        <StatTile
          label="On hold"
          value={b.hold === 'NONE' ? 'No' : 'Yes'}
          tone={b.hold === 'NONE' ? 'ok' : 'warn'}
          hint={b.hold === 'NONE' ? undefined : holdText(b.hold, b.holdReasons)}
        />
        <StatTile
          label="QC sample"
          value={qc ? statusLabel(qc.status).en : 'None'}
          tone={qc ? 'info' : 'neutral'}
          hint={qc?.seal ? `seal ${qc.seal}` : openProblems ? `${openProblems} open problems` : undefined}
        />
      </div>

      <div role="tablist" aria-label="Batch views" className="flex gap-1 border-b border-rule">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setS({ tab: t.id })}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 font-mono text-[11.5px] font-bold uppercase tracking-label',
              tab === t.id ? 'border-amber text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'details' ? (
        <DetailsTab b={b} />
      ) : (
        <TraceTab unitId={b.id} direction={tab === 'back' ? 'back' : 'forward'} depth={s.depth} onDepth={(depth) => setS({ depth })} />
      )}
    </>
  )
}

function RelativeList({ rows, empty }: { rows: BatchRelative[]; empty: string }) {
  if (rows.length === 0) return <p className="px-4 py-3 text-[14px] text-muted">{empty}</p>
  return (
    <ul className="divide-y divide-rule/70">
      {rows.map((r) => (
        <li key={`${r.unitId}:${r.runId}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-[14px]">
          <RefLink to={unitHref(r.unitId, r.unitType)}>{r.code}</RefLink>
          <span className="text-[13px] text-muted">{unitTypeLabel(r.unitType)}</span>
          <Stream stream={r.stream} />
          {r.status && <Status status={r.status} />}
          {r.runId && <RefLink to={`/c/runs/${r.runId}`} className="font-sans text-[12.5px]">Run</RefLink>}
          {r.via === 'RUN_INPUT' || r.via === 'RUN_OUTPUT' ? <Badge tone="info">Run still open</Badge> : null}
          <span className="ml-auto"><Kg grams={r.qtyG} /></span>
        </li>
      ))}
    </ul>
  )
}

function DetailsTab({ b }: { b: BatchDetail }) {
  return (
    <DetailColumns
      main={
        <>
          <Section title="Came from" bodyClassName="p-0">
            <RelativeList rows={b.parents} empty="No parents recorded." />
          </Section>
          <Section title="Made from it" bodyClassName="p-0">
            <RelativeList rows={b.children} empty="Nothing made from this batch yet." />
          </Section>
          <Section title="Sacks" bodyClassName="p-0">
            {b.sacks.length === 0 ? (
              <p className="px-4 py-3 text-[14px] text-muted">No sacks.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[14px]">
                  <caption className="sr-only">Sacks</caption>
                  <thead>
                    <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                      <th scope="col" className="px-4 py-1.5">Tag</th>
                      <th scope="col" className="px-2 py-1.5 text-right">Kg</th>
                      <th scope="col" className="px-2 py-1.5">Place</th>
                      <th scope="col" className="px-4 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.sacks.map((s) => (
                      <tr key={s.containerId} className="border-t border-rule/50">
                        <td className="px-4 py-1.5">
                          <Code>{s.tag ?? '—'}</Code>
                          {s.hold !== 'NONE' && <Badge tone="warn" className="ml-2">Sack on hold</Badge>}
                        </td>
                        <td className="px-2 py-1.5 text-right"><Kg grams={s.netG} /></td>
                        <td className="px-2 py-1.5 text-[13px]">{s.placePath ?? <span className="text-muted">—</span>}</td>
                        <td className="px-4 py-1.5"><Status status={s.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          <Section title="QC samples" bodyClassName="p-0">
            {b.samples.length === 0 ? (
              <p className="px-4 py-3 text-[14px] text-muted">No sample taken from this batch.</p>
            ) : (
              <ul className="divide-y divide-rule/70">
                {b.samples.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[14px]">
                    <span className="text-muted">Seal</span>
                    <Code>{x.seal ?? '—'}</Code>
                    {x.weightG !== null && <span className="font-mono text-[12.5px] text-muted">{x.weightG} g</span>}
                    {x.panelRequested && <span className="text-[12.5px] text-muted">panel {x.panelRequested.toLowerCase()}</span>}
                    <span className="text-[12.5px] text-muted">
                      taken <When at={x.collectedAt} />
                      {x.sentAt && (
                        <>
                          {' '}
                          · sent <When at={x.sentAt} />
                        </>
                      )}
                    </span>
                    <span className="ml-auto"><Status status={x.status} /></span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      }
      side={
        <>
          <Section title="Batch">
            <Facts
              columns={2}
              items={[
                { label: 'Kind', value: unitTypeLabel(b.unitType) },
                { label: 'Grade', value: b.grade },
                { label: 'From run', value: b.runId ? <RefLink to={`/c/runs/${b.runId}`}>{b.runCode ?? 'run'}</RefLink> : '—' },
                { label: 'Step', value: stepLabel(b.stepCode) },
                { label: 'Run status', value: b.runStatus ? <Status status={b.runStatus} /> : '—' },
                { label: 'Flush', value: b.isFlush ? 'Yes' : 'No' },
                {
                  label: 'Origin',
                  value:
                    b.stream === 'CONV' ? (
                      'Ordinary'
                    ) : (
                      <span>
                        One farmer{b.farmerCode ? <span className="font-mono"> · {b.farmerCode}</span> : ''}
                      </span>
                    ),
                },
                { label: 'Crop', value: b.cropName },
                { label: 'Season', value: b.seasonCode, mono: true },
                { label: 'On hold', value: b.hold === 'NONE' ? 'No' : (holdText(b.hold, b.holdReasons) ?? 'Yes') },
                { label: 'Gross', value: <Kg grams={b.grossG} /> },
                { label: 'Sacks weigh', value: <Kg grams={b.tareG} /> },
                { label: 'Made', value: <When at={b.committedAt} /> },
              ]}
            />
          </Section>
          <Section title="Problems" bodyClassName="p-0">
            <ExceptionTable rows={b.problems} />
          </Section>
        </>
      }
    />
  )
}

const DEPTHS = [4, 8, 16, 32, 64]

function TraceTab({ unitId, direction, depth, onDepth }: { unitId: string; direction: 'back' | 'forward'; depth: number; onDepth: (d: number) => void }) {
  const q = useTrace(unitId, direction, depth)
  const rows = q.data ? traceRows(q.data.nodes) : []
  const units = new Set(rows.map((r) => r.node.unitId)).size
  return (
    <Section
      title={direction === 'back' ? 'Trace back to the farmers' : 'Trace forward to where it went'}
      eyebrow={
        q.data
          ? direction === 'back'
            ? `${units} units · ${q.data.sourceLotIds.length} farm lot${q.data.sourceLotIds.length === 1 ? '' : 's'} · ${q.data.farmerIds.length} farmer${q.data.farmerIds.length === 1 ? '' : 's'}`
            : `${units} units`
          : undefined
      }
      actions={
        <label className="flex items-center gap-2 text-[13px] text-muted">
          Steps
          <Select
            aria-label="How many steps to follow"
            value={String(depth)}
            onChange={(v) => onDepth(Number(v))}
            options={DEPTHS.map((d) => ({ value: String(d), label: String(d) }))}
            className="h-8 w-20 text-[13px]"
          />
        </label>
      }
      bodyClassName="p-0"
    >
      <QueryState query={q} label="the trace">
        {rows.length <= 1 ? (
          <p className="px-4 py-3 text-[14px] text-muted">
            {direction === 'back' ? 'Nothing upstream of this unit.' : 'Nothing has been made from this unit yet.'}
          </p>
        ) : (
          <ol className="divide-y divide-rule/60">
            {rows.map(({ node: n, alsoFrom }) => (
              <li key={`${n.depth}:${n.unitId}`} className="px-4 py-2">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1" style={{ paddingLeft: `${Math.min(n.depth, 8) * 1.25}rem` }}>
                  {n.depth > 0 && <span aria-hidden="true" className="text-muted">↳</span>}
                  <span className="sr-only">Step {n.depth}:</span>
                  {n.depth === 0 ? <Code className="font-semibold">{n.unitCode}</Code> : <RefLink to={unitHref(n.unitId, n.unitType)}>{n.unitCode}</RefLink>}
                  <span className="text-[12.5px] text-muted">{unitTypeLabel(n.unitType)}</span>
                  <Stream stream={n.stream} />
                  {n.hold !== 'NONE' && <Status status={n.hold} />}
                  {n.isCycle && <Badge tone="warn">Loops back</Badge>}
                  <span className="ml-auto"><Kg grams={n.netG} /></span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-[12.5px] text-muted" style={{ paddingLeft: `${Math.min(n.depth, 8) * 1.25 + (n.depth > 0 ? 1.1 : 0)}rem` }}>
                  {n.farmerCode && <span>farmer <span className="font-mono">{n.farmerCode}</span></span>}
                  {n.seasonCode && <span className="font-mono">{n.seasonCode}</span>}
                  {n.runCode && n.runId && (
                    <span>
                      via <RefLink to={`/c/runs/${n.runId}`} className="text-[12.5px]">{n.runCode}</RefLink>
                      {n.runStatus && n.runStatus !== 'BALANCED' && ` (${statusLabel(n.runStatus).en.toLowerCase()})`}
                    </span>
                  )}
                  {n.qtyG !== null && n.depth > 0 && <span><Kg grams={n.qtyG} className="text-[12.5px]" /> passed</span>}
                  {n.sample ? (
                    <span>
                      sample <span className="font-mono">{n.sample.seal ?? '—'}</span> · {statusLabel(n.sample.status).en.toLowerCase()}
                      {n.sample.labOverall
                        ? ` · lab ${statusLabel(n.sample.labOverall).en.toLowerCase()}`
                        : n.sample.labReportStatus
                          ? ` · lab ${statusLabel(n.sample.labReportStatus).en.toLowerCase()}`
                          : ''}
                    </span>
                  ) : null}
                  {alsoFrom.length > 0 && <span>also from {alsoFrom.join(', ')}</span>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </QueryState>
    </Section>
  )
}
